/**
 * Holds the transient state of one agent turn, outside React.
 *
 * The point of this class is the split between its two subscriber sets:
 *
 *   - **state** changes a handful of times per turn (started, tool called,
 *     completed). The chat subscribes to this.
 *   - **text** changes on every token — 30-100 times a second. ONLY the streaming
 *     bubble subscribes to this.
 *
 * Keeping the streamed text out of React state is what stops a token from
 * re-rendering the transcript. It is also why nothing here needs `React.memo`:
 * the list's parent simply never re-renders while the answer streams.
 *
 * Read with `useSyncExternalStore`, which requires `getSnapshot` to return a
 * stable reference when nothing changed — hence the cached snapshots below.
 */
import {
  initialFormLiveState,
  reduceFormLive,
  type FormLiveState,
} from "./formLiveReducer";
import { isNewerStreamId, newestStreamId } from "./streamId";
import {
  initialResearchState,
  initialTurnState,
  reduceResearch,
  reduceTurn,
  retireResearchOnSettle,
  type BufferOp,
  type ResearchState,
  type TurnAction,
  type TurnState,
} from "./turnReducer";

type Listener = () => void;

/**
 * Tokens are coalesced into one notification per frame-ish, so the bubble repaints
 * at ~20Hz however fast the model talks. Purely a paint-rate knob: correctness does
 * not depend on it.
 */
const FLUSH_INTERVAL_MS = 50;

export class AgentTurnStore {
  private state: TurnState = initialTurnState;
  private text = "";
  /**
   * A background research job's progress, kept separate from `state` on purpose —
   * see `ResearchState`'s docstring. It has its own listener set so a research
   * event does not re-render every subscriber of the (much busier) turn state.
   */
  private research: ResearchState = initialResearchState;
  /**
   * The form-first flow's live hints (driver options, streamed concepts, "script
   * saved"). Its own listener set, for the same reason as `research`.
   */
  private form: FormLiveState = initialFormLiveState;
  /**
   * The newest stream id applied. Outside `TurnState` on purpose: that resets every
   * settle, and this has to span turns — it is what a resubscription resumes from and
   * what stops a doubly-delivered event from being applied twice. A snapshot event
   * never moves it (see `dispatch`).
   */
  private lastEventId: string | null = null;
  /**
   * The run the last settled or dismissed turn was about. `TurnState` forgets its run
   * on retirement, so without this an action response that arrives after the
   * stream already finished that run would adopt it again — a turn nothing will ever
   * complete.
   */
  private retiredRunId: string | null = null;

  /** The text actually published to React; lags `text` by up to one flush. */
  private visibleText = "";
  private flushTimer: ReturnType<typeof setTimeout> | undefined;

  private stateListeners = new Set<Listener>();
  private textListeners = new Set<Listener>();
  private researchListeners = new Set<Listener>();
  private formListeners = new Set<Listener>();

  subscribeState = (listener: Listener): (() => void) => {
    this.stateListeners.add(listener);
    return () => {
      this.stateListeners.delete(listener);
    };
  };

  subscribeText = (listener: Listener): (() => void) => {
    this.textListeners.add(listener);
    return () => {
      this.textListeners.delete(listener);
    };
  };

  subscribeResearch = (listener: Listener): (() => void) => {
    this.researchListeners.add(listener);
    return () => {
      this.researchListeners.delete(listener);
    };
  };

  subscribeForm = (listener: Listener): (() => void) => {
    this.formListeners.add(listener);
    return () => {
      this.formListeners.delete(listener);
    };
  };

  getState = (): TurnState => this.state;

  getText = (): string => this.visibleText;

  getResearch = (): ResearchState => this.research;

  getForm = (): FormLiveState => this.form;

  /**
   * The id to resume the subscription from, or null when nothing has been seen. Only
   * events that were written to the stream count: a snapshot's id is where the stream
   * stood when it was taken, not something this client has applied.
   */
  getLastEventId = (): string | null => this.lastEventId;

  dispatch = (action: TurnAction): TurnState => {
    if (action.kind === "EVENT") {
      // A stream event we have already applied — graphql-ws resubscribes on its own
      // after a reconnect and a resume replays from the last id, so the same event can
      // arrive twice. Applying a TEXT_DELTA twice duplicates text; dropping it here is
      // what makes every later handler safe to be non-idempotent.
      if (!isNewerStreamId(action.event.eventId, this.lastEventId)) {
        return this.state;
      }
      // A snapshot is applied for the state it describes, but it is not a position we
      // can resume from: its id is the stream's tail when it was taken, and everything
      // between the last real event and that tail was summarised, not delivered.
      // Resuming from it would skip that gap for good.
      if (!action.event.isSnapshot) {
        this.lastEventId = newestStreamId(
          this.lastEventId,
          action.event.eventId,
        );
      }

      const form = reduceFormLive(this.form, action.event);
      if (form !== this.form) {
        this.form = form;
        this.emitForm();
      }
    }

    if (action.kind === "SEND_ACCEPTED" && action.runId === this.retiredRunId) {
      return this.state;
    }

    const retiring =
      action.kind === "SETTLED" || action.kind === "DISMISS_ERROR";
    const { runId: previousRunId } = this.state;
    const { state, buffer } = reduceTurn(this.state, action);
    const stateChanged = state !== this.state;
    if (retiring && stateChanged) this.retiredRunId = previousRunId;
    this.state = state;

    if (action.kind === "EVENT") {
      const research = reduceResearch(this.research, action.event);
      if (research !== this.research) {
        this.research = research;
        this.emitResearch();
      }
    } else if (action.kind === "SETTLED" && stateChanged) {
      // `stateChanged` is the guard `reduceTurn` already applies to SETTLED: it
      // only resets a turn that was actually `"settling"`, so this only fires once
      // per turn, on the transition that ends it — not on a late duplicate.
      const research = retireResearchOnSettle(this.research);
      if (research !== this.research) {
        this.research = research;
        this.emitResearch();
      }
    }

    this.applyBuffer(buffer);
    if (stateChanged) this.emitState();
    return state;
  };

  /** Drop every listener and timer. Call on unmount. */
  destroy = (): void => {
    clearTimeout(this.flushTimer);
    this.flushTimer = undefined;
    this.stateListeners.clear();
    this.textListeners.clear();
    this.researchListeners.clear();
    this.formListeners.clear();
  };

  private applyBuffer(buffer: BufferOp): void {
    switch (buffer.kind) {
      case "none":
        return;
      case "clear":
        this.text = "";
        this.flushNow();
        return;
      case "set":
        this.text = buffer.text;
        this.flushNow();
        return;
      case "append":
        this.text += buffer.text;
        this.scheduleFlush();
        return;
    }
  }

  /** Terminal and structural changes publish immediately — only tokens wait. */
  private flushNow(): void {
    clearTimeout(this.flushTimer);
    this.flushTimer = undefined;
    if (this.visibleText === this.text) return;
    this.visibleText = this.text;
    this.emitText();
  }

  private scheduleFlush(): void {
    if (this.flushTimer !== undefined) return;
    this.flushTimer = setTimeout(() => {
      this.flushTimer = undefined;
      this.flushNow();
    }, FLUSH_INTERVAL_MS);
  }

  private emitState(): void {
    this.stateListeners.forEach((listener) => listener());
  }

  private emitText(): void {
    this.textListeners.forEach((listener) => listener());
  }

  private emitResearch(): void {
    this.researchListeners.forEach((listener) => listener());
  }

  private emitForm(): void {
    this.formListeners.forEach((listener) => listener());
  }
}

/** Where a reconnect resumes from, and the store (chat) it was taken from. */
export type ResumePoint = { store: AgentTurnStore; id: string | null };

/**
 * The id to resume `store`'s subscription from. A point taken from another store —
 * the chat that was open before a switch — is not this chat's, and applying it for
 * even one render would resubscribe the new chat from the old one's position.
 */
export const resumeIdFor = (
  resume: ResumePoint | null,
  store: AgentTurnStore,
): string | null => (resume?.store === store ? resume.id : null);
